import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Package, CheckCircle, XCircle, AlertCircle, TrendingUp, BarChart2 } from 'lucide-react';
import apiClient from '../../utils/api';

export const RecoveryDashboard: React.FC = () => {
  const [dashStats, setDashStats] = useState<any>(null);
  const [payments, setPayments] = useState<any[]>([]);
  const [shipments, setShipments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      apiClient.getDashboardStats(),
      apiClient.listPayments({}, 20, 0),
      apiClient.listShipments({}, 10, 0),
    ]).then(([ds, p, s]) => {
      setDashStats(ds.data.data);
      const pd = p.data.data; setPayments(pd?.payments ?? pd?.rows ?? (Array.isArray(pd) ? pd : []));
      const sd = s.data.data; setShipments(sd?.shipments ?? sd?.rows ?? (Array.isArray(sd) ? sd : []));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const py = dashStats?.payments ?? {};
  const pendingCount = py.pending?.count ?? 0;
  const pendingAmount = py.pending?.total ?? 0;
  const failedCount = py.failed?.count ?? 0;
  const failedAmount = py.failed?.total ?? 0;
  const completedCount = py.completed?.count ?? 0;
  const collectedAmount = py.completed?.total ?? 0;
  const deliverable = dashStats?.shipments?.inTransit ?? 0;

  const stats = [
    { label: 'Pending Payments', value: pendingCount, sub: `${Number(pendingAmount).toLocaleString()} RWF`, color: '#f59e0b', icon: <AlertCircle size={20} />, link: '/recovery/payments?status=PENDING' },
    { label: 'Failed Payments', value: failedCount, sub: `${Number(failedAmount).toLocaleString()} RWF`, color: '#ef4444', icon: <XCircle size={20} />, link: '/recovery/payments?status=FAILED' },
    { label: 'Collected', value: completedCount, sub: `${Number(collectedAmount).toLocaleString()} RWF`, color: '#22c55e', icon: <CheckCircle size={20} />, link: '/recovery/payments?status=COMPLETED' },
    { label: 'Ready to Deliver', value: deliverable, sub: 'in transit', color: '#0ea5e9', icon: <Package size={20} />, link: '/recovery/shipments?status=IN_TRANSIT' },
  ];

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Recovery Dashboard</h1>
        <Link to="/recovery/reports" className="btn btn-ghost"><BarChart2 size={15} /> Reports</Link>
      </div>

      {loading ? <div className="loading-spinner">Loading...</div> : (
        <>
          <div className="stats-grid">
            {stats.map(s => (
              <Link to={s.link} key={s.label} style={{ textDecoration: 'none' }}>
                <div className="stat-card" style={{ borderLeft: `4px solid ${s.color}`, cursor: 'pointer' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <div className="stat-label">{s.label}</div>
                      <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{s.sub}</div>
                    </div>
                    <div style={{ color: s.color, opacity: 0.6 }}>{s.icon}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Revenue summary bar */}
          <div className="card" style={{ marginBottom: 20, display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <TrendingUp size={18} color="#22c55e" />
              <div>
                <div style={{ fontSize: 11, color: '#64748b' }}>Total Collected</div>
                <div style={{ fontWeight: 700, fontSize: 18, color: '#22c55e' }}>{Number(collectedAmount).toLocaleString()} RWF</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CreditCard size={18} color="#f59e0b" />
              <div>
                <div style={{ fontSize: 11, color: '#64748b' }}>Pending Collection</div>
                <div style={{ fontWeight: 700, fontSize: 18, color: '#f59e0b' }}>{Number(pendingAmount).toLocaleString()} RWF</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <XCircle size={18} color="#ef4444" />
              <div>
                <div style={{ fontSize: 11, color: '#64748b' }}>Failed / Lost</div>
                <div style={{ fontWeight: 700, fontSize: 18, color: '#ef4444' }}>{Number(failedAmount).toLocaleString()} RWF</div>
              </div>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <Link to="/recovery/reports" className="btn btn-ghost" style={{ fontSize: 13 }}>
                <BarChart2 size={14} /> Full Report →
              </Link>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
            <div className="card">
              <div className="page-header" style={{ marginBottom: 16 }}>
                <h2 style={{ fontSize: 15, fontWeight: 600 }}>Pending Payments</h2>
                <Link to="/recovery/payments?status=PENDING" className="btn btn-ghost" style={{ fontSize: 12 }}>View all</Link>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Shipment</th><th>Amount</th><th>Method</th><th></th></tr></thead>
                  <tbody>
                    {payments.filter(p => p.status === 'PENDING').slice(0, 6).map((p: any) => (
                      <tr key={p.id}>
                        <td><code style={{ fontSize: 11 }}>{p.Shipment?.trackingNumber ?? p.shipment?.trackingNumber ?? p.shipmentId?.slice(0, 8)}</code></td>
                        <td style={{ fontWeight: 600 }}>{Number(p.amount).toLocaleString()} RWF</td>
                        <td>{p.paymentMethod}</td>
                        <td>
                          <Link to="/recovery/payments" className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>Manage</Link>
                        </td>
                      </tr>
                    ))}
                    {payments.filter(p => p.status === 'PENDING').length === 0 && (
                      <tr><td colSpan={4} style={{ color: '#94a3b8', textAlign: 'center', fontSize: 13 }}>No pending payments</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="page-header" style={{ marginBottom: 16 }}>
                <h2 style={{ fontSize: 15, fontWeight: 600 }}>In Transit — Ready to Deliver</h2>
                <Link to="/recovery/shipments?status=IN_TRANSIT" className="btn btn-ghost" style={{ fontSize: 12 }}>View all</Link>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Tracking #</th><th>Receiver</th><th>Amount</th><th></th></tr></thead>
                  <tbody>
                    {shipments.filter(s => s.status === 'IN_TRANSIT').slice(0, 6).map((s: any) => (
                      <tr key={s.id}>
                        <td><code style={{ fontSize: 11 }}>{s.trackingNumber}</code></td>
                        <td>{s.receiverName}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{Number(s.totalAmount).toLocaleString()} RWF</td>
                        <td>
                          <Link to={`/recovery/shipments/${s.id}/deliver`} className="btn btn-success" style={{ padding: '3px 8px', fontSize: 12 }}>Deliver</Link>
                        </td>
                      </tr>
                    ))}
                    {shipments.filter(s => s.status === 'IN_TRANSIT').length === 0 && (
                      <tr><td colSpan={4} style={{ color: '#94a3b8', textAlign: 'center', fontSize: 13 }}>No shipments in transit</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
